// src/pages/PagesUtilisateurs/DashboardUsers/DashboardUser.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import NotificationsDisplay from "./NotificationsDisplay";
import MessagesDisplay from "./MessagesDisplay";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ContactFormDisplay from "./ContactFormDisplay";
import ObjectifsSaisonDisplay from "./ObjectifsSaisonDisplay";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import {
  FaBell,
  FaEnvelope,
  FaClipboardList,
  FaBullseye,
  FaStar,
  FaCalendarAlt,
  FaMoneyBillAlt,
} from "react-icons/fa";
import { toAbsoluteTechniquePhotoUrl } from "../../../services/techniquesPhotoServices";
import { toAbsoluteEventPhotoUrl } from "../../../services/eventPhotoServices";


/* ---------- Services ---------- */
// Compteurs historiques
import { getNotificationsByUser } from "../../../services/notificationServices";
import { getMessagesByUser } from "../../../services/messageServices";
import { getAllMessagesContactForm } from "../../../services/contactformServices";

// Techniques favorites
import { getUserFavorites } from "../../../services/usersTechniquesServices";
import { getAllTechniques } from "../../../services/techniquesServices";

// Événements favoris
import { getUsersEvents } from "../../../services/usersEventsServices";
import { getAllEvents } from "../../../services/eventServices";

// Règlements
import { getReglementsByUser } from "../../../services/achatsServices";

// (Optionnel) Objectifs pour le badge “Objectifs” (non terminés)
import { getObjectifsByAdherent } from "../../../services/objectifsServices";

/* ---------- Helpers UI ---------- */

function Badge({ value, tone = "violet" }) {
  if (!value || value <= 0) return null;
  const toneMap = {
    violet: "bg-violet-600",
    amber: "bg-amber-600",
    sky: "bg-sky-600",
    rose: "bg-rose-600",
    slate: "bg-slate-600",
    emerald: "bg-emerald-600",
  };
  const bg = toneMap[tone] || toneMap.violet;
  return (
    <span
      className={`${bg} text-white text-[11px] font-bold rounded-full px-2 py-[2px] leading-none`}
    >
      {value}
    </span>
  );
}

function ModuleNav({ items, activeId, onChange }) {
  return (
    <>
      {/* Mobile */}
      <div className="md:hidden -mx-2 px-2 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 pb-2">
          {items.map((it) => {
            const Active = activeId === it.id;
            const Icon = it.icon;

            // styles d’accentuation pour messages/payments quand attention requise
            const baseIdle =
              "bg-white/80 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-white";
            const highlightClass =
              it.highlight === "messages"
                ? "bg-sky-50 ring-sky-200 dark:bg-sky-900/20"
                : it.highlight === "payments"
                ? "bg-amber-50 ring-amber-200 dark:bg-amber-900/20"
                : "";

            return (
              <button
                key={it.id}
                onClick={() => onChange(it.id)}
                className={`whitespace-nowrap inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 transition ${
                  Active
                    ? "bg-violet-600 text-white ring-violet-500"
                    : `${baseIdle} ${highlightClass}`
                }`}
                title={it.title}
              >
                <Icon />
                {it.title}
                <Badge value={it.badge} tone={it.badgeTone} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop */}
      <aside className="hidden md:block md:w-64">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <p className="font-semibold">Modules</p>
          </div>
          <nav className="p-2">
            {items.map((it) => {
              const Active = activeId === it.id;
              const Icon = it.icon;
              const baseIdle =
                "bg-white/80 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-white";
              const highlightClass =
                it.highlight === "messages"
                  ? "bg-sky-50 ring-sky-200 dark:bg-sky-900/20"
                  : it.highlight === "payments"
                  ? "bg-amber-50 ring-amber-200 dark:bg-amber-900/20"
                  : "";

              return (
                <button
                  key={it.id}
                  onClick={() => onChange(it.id)}
                  className={`w-full text-left flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm ring-1 transition mb-2 ${
                    Active
                      ? "bg-violet-600 text-white ring-violet-500"
                      : `${baseIdle} ${highlightClass}`
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon />
                    {it.title}
                  </span>
                  <Badge value={it.badge} tone={it.badgeTone} />
                </button>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}

function SectionShell({ title, subtitle, children, toolbar, className = "" }) {
  return (
    <div
      className={`rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur ${className} mb-4`}
    >
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-lg sm:text-xl font-semibold">{title}</h4>
            {subtitle && <p className="text-white/90 text-sm">{subtitle}</p>}
          </div>
          {toolbar}
        </div>
      </div>
      <div className="p-4 sm:p-6 max-h-[70vh] min-h-[300px] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

/* ---------- Utils ---------- */

const isRead = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v !== "0";
  return !!v;
};

/* ---------- Mini card Technique ---------- */

function MiniTechniqueCard({ technique }) {
  const { nom, famille, ceinture, image1_url, traduction } = technique || {};
  return (
    <div className="flex gap-3 p-3">
      <div className="shrink-0">
        <img
          src={toAbsoluteTechniquePhotoUrl(image1_url) || "/img/no-image.jpg"}
          alt={nom || "Technique"}
          className="h-20 w-20 object-cover rounded-xl ring-1 ring-slate-200 dark:ring-slate-700"
          onError={(e) => {
            e.currentTarget.src = "/img/no-image.jpg";
          }}
        />
      </div>
      <div className="min-w-0">
        <div className="font-semibold">{nom || "—"}</div>
        {traduction && (
          <div className="text-xs opacity-80 italic">{traduction}</div>
        )}
        <div className="text-xs mt-1">
          <span className="inline-flex items-center px-2 py-[2px] rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200 mr-1">
            {famille || "—"}
          </span>
          <span className="inline-flex items-center px-2 py-[2px] rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
            {ceinture || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Techniques favorites ---------- */

function FavoriteTechniquesDisplay({ userId, onCounts }) {
  const [loading, setLoading] = useState(true);
  const [techniques, setTechniques] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const prevCountRef = useRef(-1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [favRows, allTechs] = await Promise.all([
        getUserFavorites(userId).catch(() => []), // [{ user_id, technique_id }]
        getAllTechniques().catch(() => []),
      ]);
      const ids = (favRows || [])
        .map(
          (r) =>
            r.technique_id ??
            r?.Technique?.id ??
            r?.technique?.id ??
            r?.id_technique
        )
        .filter(Boolean);
      const unique = Array.from(new Set(ids));

      setFavoriteIds(unique);
      setTechniques(Array.isArray(allTechs) ? allTechs : []);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  // Mise à jour badge (sans boucler)
  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === favoriteIds.length) return;
    prevCountRef.current = favoriteIds.length;
    onCounts({ total: favoriteIds.length });
  }, [favoriteIds.length, onCounts]);

  const favorites = useMemo(() => {
    if (!favoriteIds.length || !techniques.length) return [];
    const map = new Map(techniques.map((t) => [Number(t.id), t]));
    return favoriteIds.map((id) => map.get(Number(id))).filter(Boolean);
  }, [favoriteIds, techniques]);

  if (loading) return <div>Chargement…</div>;
  if (!favorites.length)
    return (
      <div className="text-slate-600 dark:text-slate-300">
        Aucune technique favorite.
      </div>
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {favorites.map((t) => (
        <div
          key={t.id}
          className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden bg-white/60 dark:bg-slate-900/40"
        >
          <MiniTechniqueCard technique={t} />
        </div>
      ))}
    </div>
  );
}

/* ---------- Mini card Event ---------- */

function MiniEventCard({ event }) {
  const title = event?.titre || event?.title || event?.nom || "Évènement";
  const date =
    event?.date ||
    event?.date_event ||
    event?.debut ||
    event?.start_date ||
    event?.start ||
    null;
  const location =
    event?.lieu_event ||
    event?.location ||
    event?.ville ||
    event?.place ||
    null;
  const img =
    event?.photo_url ||
    event?.affiche_url ||
    event?.photo ||
    event?.cover ||
    "";

  return (
    <div className="flex gap-3 p-3">
      <div className="shrink-0">
        <img
          src={toAbsoluteEventPhotoUrl(img) || "/img/no-image.jpg"}
          alt={title}
          className="h-20 w-20 object-cover rounded-xl ring-1 ring-slate-200 dark:ring-slate-700"
          onError={(e) => {
            e.currentTarget.src = "/img/events/placeholder.jpg";
          }}
        />
      </div>
      <div className="min-w-0">
        <div className="font-semibold line-clamp-2">{title}</div>
        <div className="text-xs opacity-80 mt-1">
          {date ? new Date(date).toLocaleDateString("fr-FR") : "—"}
          {location ? <span className="ml-2">• {location}</span> : null}
        </div>
      </div>
    </div>
  );
}

/* ---------- Événements favoris ---------- */

function FavoriteEventsDisplay({ userId, onCounts }) {
  const [loading, setLoading] = useState(true);
  const [allEvents, setAllEvents] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const prevCountRef = useRef(-1);

  const extractEventId = (row) =>
    row?.event_id ?? row?.Event?.id ?? row?.event?.id ?? row?.id_event ?? null;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [favRows, events] = await Promise.all([
        getUsersEvents(userId).catch(() => []), // [{ user_id, event_id }]
        getAllEvents().catch(() => []),
      ]);

      const ids = (Array.isArray(favRows) ? favRows : [])
        .map(extractEventId)
        .filter(Boolean);
      const unique = Array.from(new Set(ids));

      setFavoriteIds(unique);
      setAllEvents(Array.isArray(events) ? events : []);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger vos évènements favoris.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  // Mise à jour badge
  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === favoriteIds.length) return;
    prevCountRef.current = favoriteIds.length;
    onCounts({ total: favoriteIds.length });
  }, [favoriteIds.length, onCounts]);

  const favorites = useMemo(() => {
    if (!favoriteIds.length || !allEvents.length) return [];
    const map = new Map(allEvents.map((e) => [Number(e.id), e]));
    return favoriteIds.map((id) => map.get(Number(id))).filter(Boolean);
  }, [favoriteIds, allEvents]);

  if (loading) return <div>Chargement…</div>;
  if (!favorites.length) {
    return (
      <div className="text-slate-600 dark:text-slate-300">
        Aucun évènement favori.
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {favorites.map((e) => (
        <div
          key={e.id}
          className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden bg-white/60 dark:bg-slate-900/40"
        >
          <MiniEventCard event={e} />
        </div>
      ))}
    </div>
  );
}

/* ---------- Règlements non réglés ---------- */

function PendingPaymentsDisplay({ userId, onCounts }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const prevCountRef = useRef(-1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await getReglementsByUser(userId);

      const pending = (Array.isArray(list) ? list : []).filter(
        (r) => String(r?.statut_reglement || "") !== "Réglé"
      );

      pending.sort(
        (a, b) => new Date(a.date_achat || 0) - new Date(b.date_achat || 0)
      );

      setRows(pending);
    } catch (e) {
      console.warn("getReglementsByUser error:", e?.response?.status || e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  // Mise à jour badge (sans boucler même si onCounts change de ref)
  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === rows.length) return;
    prevCountRef.current = rows.length;
    onCounts({ total: rows.length });
  }, [rows.length, onCounts]);

  if (loading) return <div>Chargement…</div>;
  if (!rows.length)
    return (
      <div className="text-slate-600 dark:text-slate-300">
        Aucun règlement en attente.
      </div>
    );

  const pillTone = (statut) => {
    if (statut === "En Attente")
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200";
    if (statut === "Non Réglé")
      return "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200";
    return "bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-200";
  };

  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const article = r.Article || null;
        const hasArticle = r.article_id != null;
        const title =
          article?.nom || r.message_achat || r.type_achat || "Achat";
        const imgUrl = hasArticle
          ? article?.photo_url || "/img/shop/placeholder.jpg"
          : "/img/no-image.jpg";
        const montant = r.prix_total != null ? Number(r.prix_total) : null;
        const date = r.date_achat
          ? new Date(r.date_achat).toLocaleDateString("fr-FR")
          : null;

        return (
          <div
            key={r.id}
            className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/60 dark:bg-slate-900/40 p-4 flex items-center gap-4"
          >
            <div className="shrink-0">
              <img
                src={imgUrl}
                alt={title}
                className="h-16 w-16 object-cover rounded-xl ring-1 ring-slate-200 dark:ring-slate-700"
                onError={(e) => {
                  e.currentTarget.src = "/img/no-image.jpg";
                }}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{title}</div>
              <div className="text-xs opacity-80 mt-1">
                {r.type_achat ? (
                  <span className="mr-2">{r.type_achat}</span>
                ) : null}
                <span
                  className={`inline-flex items-center px-2 py-[1px] rounded-full ${pillTone(
                    r.statut_reglement
                  )}`}
                >
                  {r.statut_reglement}
                </span>
                {date ? (
                  <span className="ml-2 opacity-70">• {date}</span>
                ) : null}
              </div>
            </div>

            {montant != null && (
              <div className="shrink-0 font-semibold">
                {montant.toFixed(2)} €
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Page ---------- */

export default function DashboardUser() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // userId
  const [userId, setUserId] = useState(null);
  useEffect(() => {
    try {
      const data = localStorage.getItem("userInfo");
      if (data) {
        const obj = JSON.parse(data);
        setUserId(obj.userId || null);
      } else {
        toast.error(
          "Vous devez être connecté pour accéder au tableau de bord."
        );
      }
    } catch {
      toast.error("Erreur de lecture des informations utilisateur.");
    }
  }, []);

  // Onglet actif
  const [active, setActive] = useState(() => {
    return localStorage.getItem("dash_user_active_tab") || "notifications";
  });
  useEffect(() => {
    localStorage.setItem("dash_user_active_tab", active);
  }, [active]);

  // Compteurs
  const [counts, setCounts] = useState({
    payments: { total: 0 },
    notifications: { total: 0, unread: 0 },
    messages: { total: 0, unread: 0 },
    contact: { total: 0, unread: 0 },
    objectifs: { total: 0 }, // nouveau badge
    techniques: { total: 0 },
    events: { total: 0 },
  });

  const isAdminOrTeacher = userRole === "admin" || userRole === "enseignant";

  // Préchargement compteurs (historiques)
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const loadCounts = async () => {
      try {
        const [notifs, msgs, contact] = await Promise.all([
          getNotificationsByUser(userId).catch(() => []),
          getMessagesByUser(userId).catch(() => []),
          isAdminOrTeacher
            ? getAllMessagesContactForm(userId).catch(() => [])
            : Promise.resolve([]),
        ]);

        if (cancelled) return;

        const notifUnread = (notifs || []).reduce(
          (acc, n) => acc + (isRead(n.read_notification) ? 0 : 1),
          0
        );

        const msgsFiltered = (msgs || []).filter((m) => !m.is_copy);
        const msgUnread = msgsFiltered.reduce(
          (acc, m) => acc + (isRead(m.read_message) ? 0 : 1),
          0
        );

        const contactUnread = (contact || []).reduce(
          (acc, m) => acc + (isRead(m.read_message) ? 0 : 1),
          0
        );

        setCounts((c) => ({
          ...c,
          notifications: { total: (notifs || []).length, unread: notifUnread },
          messages: { total: msgsFiltered.length, unread: msgUnread },
          contact: { total: (contact || []).length, unread: contactUnread },
        }));
      } catch (e) {
        console.error("Erreur préchargement compteurs:", e);
      }
    };

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [userId, isAdminOrTeacher]);

  // ✅ Préchargement des autres compteurs (objectifs, techniques, events, payments)
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const preloadOthers = async () => {
      try {
        const [favTechs, favEvents, regls, objectifs] = await Promise.all([
          getUserFavorites(userId).catch(() => []),
          getUsersEvents(userId).catch(() => []),
          getReglementsByUser(userId).catch(() => []),
          // Objectifs non terminés (si service dispo)
          getObjectifsByAdherent
            ? getObjectifsByAdherent(userId, { is_done: false }).catch(() => [])
            : Promise.resolve([]),
        ]);

        if (cancelled) return;

        // Techniques
        const techIds = Array.from(
          new Set(
            (favTechs || [])
              .map(
                (r) =>
                  r.technique_id ??
                  r?.Technique?.id ??
                  r?.technique?.id ??
                  r?.id_technique
              )
              .filter(Boolean)
          )
        );

        // Évènements
        const eventIds = Array.from(
          new Set(
            (favEvents || [])
              .map((r) => r?.event_id ?? r?.Event?.id ?? r?.event?.id)
              .filter(Boolean)
          )
        );

        // Paiements en attente
        const pendingCount = (regls || []).filter(
          (r) => String(r?.statut_reglement || "") !== "Réglé"
        ).length;

        // Objectifs non terminés
        const objectifsCount = Array.isArray(objectifs) ? objectifs.length : 0;

        setCounts((c) => ({
          ...c,
          techniques: { total: techIds.length },
          events: { total: eventIds.length },
          payments: { total: pendingCount },
          objectifs: { total: objectifsCount },
        }));
      } catch (e) {
        // en cas d’erreur, on n’empêche pas l’affichage des autres modules
        console.warn("Préchargement autres compteurs:", e?.message || e);
      }
    };

    preloadOthers();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // callbacks stables pour les sous-modules (évite les boucles)
  const setModuleCounts = useCallback((key, obj) => {
    setCounts((c) => ({ ...c, [key]: { ...c[key], ...obj } }));
  }, []);
  const onCountsNotifications = useCallback(
    (obj) => setModuleCounts("notifications", obj),
    [setModuleCounts]
  );
  const onCountsMessages = useCallback(
    (obj) => setModuleCounts("messages", obj),
    [setModuleCounts]
  );
  const onCountsContact = useCallback(
    (obj) => setModuleCounts("contact", obj),
    [setModuleCounts]
  );
  const onCountsTechniques = useCallback(
    (obj) => setModuleCounts("techniques", obj),
    [setModuleCounts]
  );
  const onCountsEvents = useCallback(
    (obj) => setModuleCounts("events", obj),
    [setModuleCounts]
  );
  const onCountsPayments = useCallback(
    (obj) => setModuleCounts("payments", obj),
    [setModuleCounts]
  );

  // Modules + mise en évidence des onglets Messages/Payments si besoin
  const modules = useMemo(
    () =>
      [
        {
          id: "payments",
          title: "Règlements à effectuer",
          icon: FaMoneyBillAlt,
          badge: counts.payments?.total ?? 0,
          badgeTone: "amber",
          highlight: (counts.payments?.total ?? 0) > 0 ? "payments" : null,
        },
        {
          id: "messages",
          title: "Messages",
          icon: FaEnvelope,
          badge: counts.messages?.unread ?? 0,
          badgeTone: "violet",
          highlight: (counts.messages?.unread ?? 0) > 0 ? "messages" : null,
        },
        {
          id: "notifications",
          title: "Notifications",
          icon: FaBell,
          badge: counts.notifications?.unread ?? 0,
          badgeTone: "violet",
        },
        {
          id: "objectifs",
          title: "Objectifs Saison",
          icon: FaBullseye,
          badge: counts.objectifs?.total ?? 0,
          badgeTone: "emerald",
        },
        {
          id: "events",
          title: "Évènements favoris",
          icon: FaCalendarAlt,
          badge: counts.events?.total ?? 0,
          badgeTone: "emerald",
        },
        {
          id: "techniques",
          title: "Techniques favorites",
          icon: FaStar,
          badge: counts.techniques?.total ?? 0,
          badgeTone: "emerald",
        },
        isAdminOrTeacher && {
          id: "contact",
          title: "Formulaires",
          icon: FaClipboardList,
          badge: counts.contact?.unread ?? 0,
          badgeTone: "violet",
        },
      ].filter(Boolean),
    [counts, isAdminOrTeacher]
  );

  if (!userId) {
    return (
      <div className="w-full">
        <div className="pt-[150px]" aria-hidden />
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
          <div className="text-center py-16 text-slate-700 dark:text-slate-200">
            Vous devez être connecté pour accéder à cette page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="w-full">
      <ScrollIndicator />
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Tableau de bord"
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[16rem,1fr] gap-4 md:gap-6">
          <ModuleNav items={modules} activeId={active} onChange={setActive} />

          <div className="space-y-6">
            {active === "payments" && (
              <SectionShell
                title="Règlements à effectuer"
                subtitle="Achats encore non réglés. Vous pouvez les régler en ligne sur la page des Commandes & Règlements."
              >
                <PendingPaymentsDisplay
                  userId={userId}
                  onCounts={onCountsPayments}
                />
              </SectionShell>
            )}
            {active === "notifications" && (
              <SectionShell
                title="Notifications"
                subtitle="Toutes vos notifications importantes, regroupées ici."
              >
                <NotificationsDisplay
                  userId={userId}
                  onCounts={onCountsNotifications}
                />
              </SectionShell>
            )}

            {active === "messages" && (
              <SectionShell
                title="Messages"
                subtitle="Conversations et annonces internes. Vous pouvez envoyer un message aux enseignants ou à l’administrateur depuis la page des Messages."
              >
                <MessagesDisplay userId={userId} onCounts={onCountsMessages} />
              </SectionShell>
            )}

            {active === "objectifs" && (
              <SectionShell
                title="Objectifs de saison"
                subtitle="Définissez vos ambitions (loisir, ceinture, compétition, kata, UV...). Suivez l’avancement. Ces objectifs sont partagés avec les enseignants."
              >
                <ObjectifsSaisonDisplay userId={userId} />
              </SectionShell>
            )}

            {active === "techniques" && (
              <SectionShell
                title="Techniques favorites"
                subtitle="Vos techniques favorites, tirées de la bibliothèque et affichées ici."
              >
                <FavoriteTechniquesDisplay
                  userId={userId}
                  onCounts={onCountsTechniques}
                />
              </SectionShell>
            )}

            {active === "events" && (
              <SectionShell
                title="Évènements favoris"
                subtitle="Vos évènements suivis / favoris."
              >
                <FavoriteEventsDisplay
                  userId={userId}
                  onCounts={onCountsEvents}
                />
              </SectionShell>
            )}

            {active === "contact" && isAdminOrTeacher && (
              <SectionShell
                title="Formulaires de contact"
                subtitle="Réponses et suivis des demandes reçues."
              >
                <ContactFormDisplay
                  userId={userId}
                  onCounts={onCountsContact}
                />
              </SectionShell>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
}
